/**
 * @file cardboard/transforms.js
 * @author Geoffrey Pursell
 * @desc Contains functions that transform data in various ways.
*/

define('cardboard/transforms',[
    // libraries
    "underscore",
    // app modules
    "cardboard/util"
],
    function (_,
        Util) {

        console.log("MODULE: cardboard/transforms");

        /**
         * @exports cardboard/transforms
         * @desc Contains functions that transform data in various ways.
         */

        var Transforms = {

            /** @namespace */
            ToChart: {

                // new transforms

                pointsAcrossRows: function (inputData, viewInfo) {
                    if (!inputData) { return null; }
                    return Transforms.ToChart.makeMultipleSeriesFromAcrossRows(
                        this.normalizeSeriesCollection(inputData, viewInfo),
                        viewInfo
                    );
                },
                pointsWithinRows: function (inputData, viewInfo) {
                    return Transforms.ToChart.makeMultipleSeriesFromWithinRows(
                        this.normalizeSeriesCollection(inputData, viewInfo),
                        viewInfo
                    );
                },

                normalizeSeriesCollection: function (inputData, viewInfo) {
                    if (!inputData) { return null; }

                    // default dataDimensions is 1.
                    var dataDimensions = (viewInfo.dataDimensions || 1);

                    // if the number of data dimensions is 1, we should wrap the
                    // data array in another array so the loops, which expect two
                    // dimensions, work correctly
                    inputData.data = (dataDimensions === 1 ? [inputData.data] : inputData.data);

                    return inputData;
                },

                /** takes a data source in which each given row is already a data
                 * point in a series to be charted, and constructs the chartable
                 * series from that.
                 * @param {Object} inputData an object containing data and possibly
                 * metadata properties. Data will be an array (each element of which
                 * constitutes a series) of series data (each element of which is
                 * either an array of data points (each of which is an object), or
                 * an object that has such an array somewhere inside it that we find
                 * by using the seriesInfo.dataPath). Like this:
                 *
                 *	 +---------+
                 *	 | X X X X |
                 *	 | . . . . |
                 *	 | . . . . |
                 *	 | . . . . |
                 *	 +---------+
                 *
                 * @param {Object} viewInfo the chart's view info object, containing
                 * all the path information we need to find the relevant data and
                 * set up the series
                 */
                makeMultipleSeriesFromWithinRows: function (inputData, viewInfo) {
                    var seriesDefinitions = viewInfo.series;

                    // Loop over each expected series. For line charts there
                    // could be 1-3 of these.
                    if (!seriesDefinitions) {
                        throw "No series in this plugin's view info!";
                    } else if (seriesDefinitions.length === 1) {
                        // clone a series definition for each hunk of input data
                        seriesDefinitions = expandSeriesDefinitions(inputData, seriesDefinitions, viewInfo);
                    }

                    return _(seriesDefinitions)
                        .map(function (seriesInfo, index) {
                            // there should be as many "rows" in the inputData we were
                            // given as there are series to be plotted. right?
                            var seriesData = _.isArray(inputData.data) ? inputData.data[index] : inputData.data;

                            return makeSingleChartableSeries(seriesData, inputData.meta, viewInfo, seriesInfo);
                        })
                        .reject(function (chartableSeries) {
                            // get rid of empty series before we return
                            return chartableSeries.data.length === 0;
                        })
                        .value();
                },

                /** produces an array of data series in the format that the chart
                 * library wants for a stackedColumn. In achievement, for example,
                 * this takes data that is presented with one row per subject, and
                 * columns (categories) for each performance category, and shifts it
                 * so that it's presented with one row (series) per performance
                 * category, and columns (categories) for each subject.  NOTE: this
                 * also function as a "make multiple series without any
                 * rows". Grouped column (college report) uses it this way.
                 * @param {Object} inputData - an object with properties data and
                 * optionally meta. The "data" property is an array of arrays or
                 * array of objects containing a nested array; in this version of
                 * the transform, we build series by iterating ACROSS these
                 * "rows". For example, the first series would be made of value 0 of
                 * each nested array, like this:
                 *
                 *	 +---------+
                 *	 | X . . . |
                 *	 | X . . . |
                 *	 | X . . . |
                 *	 | X . . . |
                 *	 +---------+
                 * 
                 * @param {Object} viewInfo - the chart's view info object, containing
                 * all the path information we need to find the relevant data and
                 * set up the series
                 */

                makeMultipleSeriesFromAcrossRows: function (inputData, viewInfo) {
                    if (inputData === null) {
                        throw "No series in this plugin's view info!";
                    }
                    //console.log("inputData:", inputData);
                    // console.log("viewInfo:", viewInfo);
                    return _(viewInfo.series)
                        .map(function (seriesInfo) {
                            // there should be as many rows in the inputData we were
                            // given as there are series to be plotted. right?

                            if (inputData)
                                return makeSingleChartableSeries(inputData.data, inputData.meta, viewInfo, seriesInfo);
                        })
                        .reject(function (chartableSeries) {
                            // get rid of empty series before we return
                            return chartableSeries.data.length === 0;
                        })
                        .value();
                }
            },

            /**
             * read the data series from the model object and choose a transform
             * function that will produce the format that HighCharts expects. Every
             * different data source may have a slightly different transform.
             * @param {Array | Object} data The raw data to be adapted
             * @param {Object} viewInfo The plugin's pane's chart view info.
             */
            adaptDataForCharting: function (data, viewInfo) {
                // the default data shape is "pointsWithinRows".
                var dataShape = (viewInfo.dataShape ? viewInfo.dataShape : "pointsWithinRows"),
                    transformer = Transforms.ToChart[dataShape];
                return transformer.call(Transforms.ToChart, data, viewInfo);
            }
        };

        // private functions

        /**
         * takes a chartable point object and nulls out any non-chartable
         * points so that they don't render instead of rendering as zeros
         * @param {Object} seriesInfo the plugin's pane's chart view's series info object
         * @param {Object} point the data point to be plotted; must contain an allInfo property
         * @memberof module:transforms
         * @private */
        function nullifyNonChartablePoint(seriesInfo, point) {
            var stringValue = getDataFromPath(point.allInfo, seriesInfo.stringValuePath);

            if (stringValue === "N/A" || stringValue === "CTSTR" || stringValue === "SPP" || point.y === "" || ((seriesInfo.stringValuePath === "averageScaleScoreDisplayValue") && (point.y === 0))) {
                point.y = null;
            }
            return point;
        }

        /**
         * when we've got a one-length array of series definitions, it means that
         * that series definition should repeat in place for each data series we
         * find in the input data. Duplicating them here, on the fly, as approriate
         * keeps subsequent codepaths simple and stable.
         * @param {Array | Object} inputData - the collection of series we expect
         * from the data
         * @param {Array} seriesDefinitions - the array of objects that describe
         * chartable series, from the plugin
         * @memberof module:transforms
         * @private */
        function expandSeriesDefinitions(inputData, seriesDefinitions, viewInfo) {
            if (!inputData) { return null; }
            var rows = _.values(inputData.data), // to account for either array or hash input
                originalSeriesDefinition = seriesDefinitions[0];

            return _(rows)
                .map(function () { return _.clone(originalSeriesDefinition); })
                .each(function (series, index) {
                    if (series.colors && !viewInfo.colorByPoint) {
                        series.color = series.colors[index];
                        delete series.colors;
                    }
                })
                .value();
        }

        /**
         * Creates a single chartable series. Vetted so far for:line, stackedColumn, pie, column
         * @param {Array} inputRows The rows of raw data to be turned into points in
         * a series; each row will become one point in the series.
         * @param {Object} viewInfo The plugin's pane's chart view info. We need
         * this to know where to look in the data for the x axis labels.
         * @param {Object} seriesInfo The plugin's pane's chart view's series info
         * object. this tells us everything about where to look in each inputRow for
         * the data to be plotted.
         * @memberof module:transforms
         * @private */

        function makeSingleChartableSeries(seriesData, metaData, viewInfo, seriesInfo) {
            // for each series, find the blob that contains the
            // values we need.  seriesInfo.dataPath may point to the
            // real chartable data

            var inputRows = (seriesInfo.dataPath ?
                getDataFromPath(seriesData, seriesInfo.dataPath) :
                seriesData),
                seriesDisplayName = seriesInfo.seriesDisplayName ||
                    getDataFromPath(metaData, seriesInfo.seriesDisplayNamePath) ||
                    getDataFromPath(seriesData, seriesInfo.seriesDisplayNamePath),

                chartableSeries = {
                    // the name of the series will be whatever's at the requested
                    // path in the first row of input data; they should be all the
                    // same
                    name: seriesDisplayName,
                    color: seriesData.color,
                    data: seriesInfo.values ?
                        makeSingleChartableSeriesDataManually(inputRows, metaData, viewInfo, seriesInfo) :
                        makeSingleChartableSeriesData(inputRows, metaData, viewInfo, seriesInfo)
                };

            // if the whole series wants to be a certain color, do that
            if (seriesInfo.color) {
                chartableSeries.color = Util.Color.colorForChartType(viewInfo.type, seriesInfo.color);
            }
            if (seriesInfo.colorPath) {
                requestedColor = chartableSeries.color;
                chartableSeries.color = Util.Color.colorForChartType(viewInfo.type, requestedColor);
            }

            // if indexes are given by the series info for series ordering, we
            // should take 'em
            if (seriesInfo.index) {
                chartableSeries.index = seriesInfo.index;
            }

            if (seriesInfo.legendIndex) {
                chartableSeries.legendIndex = seriesInfo.legendIndex;
            }

            chartableSeries.config = seriesInfo;
            return chartableSeries;
        }

        function makeSingleChartableSeriesData(inputRows, metaData, viewInfo, seriesInfo) {
            var sortInfo = viewInfo.seriesSort;

            var _output = _(inputRows).map(function (row, index) {
                // each point is mapped from the input data
                var point = {};

                // y: the charted value itself
                point.y = getDataFromPath(row, seriesInfo.valuePath);

                // The point name. This one is a little spooky.
                // - Stacked columns label each point with a view-wide
                // xAxisLabelPath that will get a value from the same place for
                // every series.
                // - Line charts use a series-specific displayNamePath to label each
                // line.
                // - Other types use the displayNamePath to simple label each data
                // point: simple columns or pie segments, for example.
                point.name = getDataFromPath(row, viewInfo.xAxisLabelPath ||
                    seriesInfo.displayNamePath);
                if (viewInfo.xAxisLabel)
                    point.name = viewInfo.xAxisLabel;

                // allInfo: annotate this point with everything we know
                // about it, in case we need it later. Sometimes we do.
                point.allInfo = row;
                point.allInfo.meta = metaData;

                // OPTIONAL ATTRIBUTES, only added if requested by the seriesInfo

                // long name
                if (seriesInfo.longDisplayNamePath) {
                    point.longName = getDataFromPath(row, seriesInfo.longDisplayNamePath);
                }
                if (seriesInfo.colorPath) {

                    requestedColor = getDataFromPath(row, seriesInfo.colorPath);
                    point.color = Util.Color.colorForChartType(viewInfo.type, requestedColor);

                }
                else {
                    setPointColor(seriesInfo, viewInfo, index, point);
                }
                // if each point wants to be a certain color, do that. the
                // seriesInfo will tell us this with an array of colors

                return nullifyNonChartablePoint(seriesInfo, point);
            });

            if (sortInfo) {
                // sortBy sorts ascending by default
                _output = _output.sortBy(function (point) {
                    return getDataFromPath(point.allInfo, sortInfo.by);
                });

                // so, if descending sort was requested, just reverse it
                if (sortInfo.order === "descending") {
                    _output = _output.reverse();
                }
            }

            //console.log(_output);
            if ((_output.__wrapped__[0]) && (_output.__wrapped__[0].name == "Exceeds"))
                _output.__wrapped__[0].name = "Statewide";

            return _output.value();
        }

        // this is for when the series info is requesting that we build a series "by
        // hand", by giving points with labels (or maybe label paths) and value
        // paths
        function makeSingleChartableSeriesDataManually(inputData, metaData, viewInfo, seriesInfo) {
            // note that we have to iterate over the pointInfos from series.values,
            // rather than input rows.
            return _(seriesInfo.values)
                .filter(function (pointInfo) {
                    // filter out points that have an unmet visibleWhen condition
                    var condition = pointInfo.visibleWhen;
                    if (condition) {
                        return Util.evaluateFilterCondition(condition, inputData);
                    } else {
                        return true;
                    }
                })
                .map(function (pointInfo, index) {
                    // each point is mapped from the input data
                    var point = {};

                    // y: the charted value itself
                    point.y = getDataFromPath(inputData, pointInfo.valuePath);

                    // CHARBY: 01/21/2015
                    // Null values in Y coords was causing major headaches with CTSTR values.
                    // Assigning to 0 here so that graphs have something to plot.
                    if (point.y === null) { point.y = 0; }

                    // The point name. This one is a little spooky.
                    // - Stacked columns label each point with a view-wide
                    // xAxisLabelPath that will get a value from the same place for
                    // every series.
                    // - Line charts use a series-specific displayNamePath to label each
                    // line.
                    // - Other types use the displayNamePath to simple label each data
                    // point: simple columns or pie segments, for example.
                    point.name = pointInfo.pointLabel ||
                        getDataFromPath(inputData, pointInfo.pointLabelPath);

                    // allInfo: annotate this point with everything we know
                    // about it, in case we need it later. Sometimes we do.
                    point.allInfo = inputData;

                    // OPTIONAL ATTRIBUTES, only added if requested by the seriesInfo

                    // long name
                    if (pointInfo.longDisplayNamePath) {
                        point.longName = getDataFromPath(inputData, pointInfo.longDisplayNamePath);
                    }

                    // if each point wants to be a certain color, do that. the
                    // seriesInfo will tell us this with an array of colors
                    setPointColor(seriesInfo, viewInfo, index, point);

                    return nullifyNonChartablePoint(seriesInfo, point);
                })
                .filter(function (point) {
                    // GTP, 6/19/2014: this was changed because it was filtering out
                    // legitimate zeroes
                    // return point.y;
                    return ((_.isNumber(point.y) && !_.isNaN(point.y)) || (seriesInfo.stringValuePath === "averageScaleScoreDisplayValue"));
                })
                .value();
        }

        function setPointColor(seriesInfo, viewInfo, index, point) {
            if (seriesInfo.colors) {
                var requestedColor = seriesInfo.colors[index % seriesInfo.colors.length];
                point.color = Util.Color.colorForChartType(viewInfo.type, requestedColor);
            }

        }

        // shortcuts
        var getDataFromPath = Util.Data.getDataFromPath;

        return Transforms;
    });

